import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast.tsx';
import { Modal } from '../components/Modal';
import type { Local, CreateLocalRequest, UpdateLocalRequest } from '../types';
import { locaisApi } from '../services/api';
import { Plus, Edit, Trash2, MapPin } from 'lucide-react';

const COLOR_PRESETS = [
  '#C9A86A', '#D4AF37', '#8B5CF6', '#DB2777',
  '#059669', '#0284C7', '#EA580C', '#7C3AED',
  '#0D9488', '#DC2626',
];

interface LocalForm {
  descricao: string;
  cor: string;
  mesaInicio: string;
  mesaFim: string;
  capacidadePadrao: string;
}

const emptyForm: LocalForm = { descricao: '', cor: COLOR_PRESETS[0], mesaInicio: '1', mesaFim: '20', capacidadePadrao: '4' };

export function LocaisPage() {
  const { isAdmin } = useAuth();
  const { success, error } = useToast();
  const [locais, setLocais] = useState<Local[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState<Local | null>(null);
  const [form, setForm] = useState<LocalForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const loadLocais = useCallback(async () => {
    try {
      const response = await locaisApi.getAll();
      if (response.success && response.data) {
        setLocais(response.data);
      }
    } catch {
      error('Erro ao carregar locais');
    } finally {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const response = await locaisApi.getAll();
        if (active && response.success && response.data) {
          setLocais(response.data);
        }
      } catch {
        if (active) error('Erro ao carregar locais');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [error]);

  const openNovo = () => {
    setForm(emptyForm);
    setEditando(null);
    setFormError('');
    setModalOpen(true);
  };

  const openEditar = (local: Local) => {
    setForm({
      descricao: local.descricao,
      cor: local.cor,
      mesaInicio: String(local.mesaInicio ?? 1),
      mesaFim: String(local.mesaFim ?? 20),
      capacidadePadrao: String(local.capacidadePadrao ?? 4),
    });
    setEditando(local);
    setFormError('');
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.descricao.trim()) {
      setFormError('A descrição é obrigatória');
      return;
    }
    const inicio = Number(form.mesaInicio);
    const fim = Number(form.mesaFim);
    const cap = Number(form.capacidadePadrao);
    if (!inicio || !fim || inicio < 1 || fim < inicio || fim - inicio > 500) {
      setFormError('Intervalo de mesas inválido (ex. 1 a 100)');
      return;
    }
    if (!cap || cap < 1 || cap > 50) {
      setFormError('Capacidade deve ser entre 1 e 50');
      return;
    }

    setSaving(true);
    try {
      if (editando) {
        const request: UpdateLocalRequest = {
          descricao: form.descricao.trim(),
          cor: form.cor,
          mesaInicio: inicio,
          mesaFim: fim,
          capacidadePadrao: cap,
          versao: editando.versao || 1,
        };
        const response = await locaisApi.update(editando.id, request);
        if (response.success) {
          success('Local atualizado — mesas sincronizadas');
          setModalOpen(false);
          loadLocais();
        } else {
          setFormError(response.message || 'Erro ao atualizar local');
        }
      } else {
        const request: CreateLocalRequest = {
          descricao: form.descricao.trim(),
          cor: form.cor,
          mesaInicio: inicio,
          mesaFim: fim,
          capacidadePadrao: cap,
        };
        const response = await locaisApi.create(request);
        if (response.success) {
          success(`Local criado com mesas ${inicio}–${fim}`);
          setModalOpen(false);
          loadLocais();
        } else {
          setFormError(response.message || 'Erro ao criar local');
        }
      }
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Erro ao guardar local');
    } finally {
      setSaving(false);
    }
  };

  const handleEliminar = async (local: Local) => {
    if (!confirm(`Eliminar o local "${local.descricao}"?`)) return;
    try {
      const response = await locaisApi.delete(local.id);
      if (response.success) {
        success('Local eliminado');
        loadLocais();
      } else {
        error(response.message || 'Erro ao eliminar local');
      }
    } catch {
      error('Erro ao eliminar local');
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Locais</h1>
        <div className="header-actions">
          {isAdmin && (
            <button className="btn btn-primary" onClick={openNovo}>
              <Plus className="w-4 h-4" />
              Novo Local
            </button>
          )}
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="page-loading">
            <div className="spinner"></div>
            <p>A carregar locais...</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Cor</th>
                  <th>Descrição</th>
                  <th>Intervalo mesas</th>
                  <th>Cap. padrão</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {locais.map(local => (
                  <tr key={local.id}>
                    <td>
                      <div className="color-cell">
                        <span className="color-swatch" style={{ backgroundColor: local.cor }}></span>
                        <span className="text-sm mono">{local.cor}</span>
                      </div>
                    </td>
                    <td>{local.descricao}</td>
                    <td className="mono text-sm">{local.mesaInicio}–{local.mesaFim} <span className="text-sm" style={{ color: 'var(--c-text-muted)' }}>({(local.mesaFim - local.mesaInicio + 1)} mesas)</span></td>
                    <td>{local.capacidadePadrao} pax</td>
                    <td>
                      {isAdmin ? (
                        <div className="table-actions">
                          <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEditar(local)} aria-label="Editar">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button className="btn btn-ghost btn-sm btn-icon text-red-600" onClick={() => handleEliminar(local)} aria-label="Eliminar">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <span className="text-sm">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && locais.length === 0 && (
          <div className="empty-state">
            <MapPin className="w-12 h-12 text-gray-300" />
            <h3>Nenhum local configurado</h3>
            <p>Adicione o primeiro local</p>
            {isAdmin && (
              <button className="btn btn-primary mt-4" onClick={openNovo}>
                <Plus className="w-4 h-4" />
                Novo Local
              </button>
            )}
          </div>
        )}
      </div>

      <Modal
        title={editando ? 'Editar Local' : 'Novo Local'}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              Cancelar
            </button>
            <button type="submit" form="local-form" className="btn btn-primary" disabled={saving}>
              {saving ? 'A guardar...' : editando ? 'Atualizar' : 'Criar'}
            </button>
          </>
        }
      >
        <form id="local-form" onSubmit={handleSubmit} noValidate>
          {formError && (
            <div className="form-error" role="alert">
              {formError}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="descricao" className="label">Descrição <span className="required">*</span></label>
            <input
              type="text"
              id="descricao"
              className="input"
              value={form.descricao}
              onChange={e => setForm(p => ({ ...p, descricao: e.target.value }))}
              placeholder="Ex: Sala Principal, Esplanada..."
              required
            />
          </div>

          <div className="form-group">
            <label className="label">Cor <span className="required">*</span></label>
            <div className="color-picker">
              {COLOR_PRESETS.map(cor => (
                <button
                  key={cor}
                  type="button"
                  className={`color-dot ${form.cor === cor ? 'active' : ''}`}
                  style={{ backgroundColor: cor }}
                  onClick={() => setForm(p => ({ ...p, cor }))}
                  aria-label={`Cor ${cor}`}
                ></button>
              ))}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="mesaInicio" className="label">Mesa inicial <span className="required">*</span></label>
              <input
                type="number"
                id="mesaInicio"
                className="input"
                value={form.mesaInicio}
                onChange={e => setForm(p => ({ ...p, mesaInicio: e.target.value }))}
                min={1}
                max={999}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="mesaFim" className="label">Mesa final <span className="required">*</span></label>
              <input
                type="number"
                id="mesaFim"
                className="input"
                value={form.mesaFim}
                onChange={e => setForm(p => ({ ...p, mesaFim: e.target.value }))}
                min={1}
                max={999}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="capacidadePadrao" className="label">Capacidade padrão</label>
            <input
              type="number"
              id="capacidadePadrao"
              className="input"
              value={form.capacidadePadrao}
              onChange={e => setForm(p => ({ ...p, capacidadePadrao: e.target.value }))}
              min={1}
              max={20}
            />
            <span className="form-hint">Ao gravar serão criadas as mesas {form.mesaInicio || '?'}–{form.mesaFim || '?'} ({Number(form.mesaFim) - Number(form.mesaInicio) + 1 || '?'} mesas). Mesas fora do intervalo ficam desabilitadas (não apagadas).</span>
          </div>
        </form>
      </Modal>
    </div>
  );
}
